import { useEffect, useRef, type CSSProperties, type ReactNode } from 'react';

import type { OrderListItem } from './api/types.ts';
import { formatPara, para, ratio } from './core/money.ts';
import { OrderStatusGroup } from './core/order-status.ts';
import { useStore } from './core/store.ts';
import { clockTime, errorText, shortStatusText, statusText, useLang, useStrings, type Strings } from './i18n/index.ts';
import { AddressGateSheet } from './screens/AddressGateSheet.tsx';
import { AddressesScreen } from './screens/AddressesScreen.tsx';
import { CardsScreen } from './screens/CardsScreen.tsx';
import { AgeGateSheet } from './screens/AgeGateSheet.tsx';
import { CartSheet } from './screens/CartSheet.tsx';
import { CatalogScreen } from './screens/CatalogScreen.tsx';
import { CategoryScreen, ProductListScreen } from './screens/CategoryScreen.tsx';
import { HomeScreen } from './screens/HomeScreen.tsx';
import { NewAddressScreen } from './screens/NewAddressScreen.tsx';
import { NotificationsScreen } from './screens/NotificationsScreen.tsx';
import { OrderScreen } from './screens/OrderScreen.tsx';
import { OrdersScreen } from './screens/OrdersScreen.tsx';
import { PartnerInterstitial } from './screens/PartnerInterstitial.tsx';
import { PushPrimerSheet } from './screens/PushPrimerSheet.tsx';
import { ProductScreen } from './screens/ProductScreen.tsx';
import { ProfileScreen } from './screens/ProfileScreen.tsx';
import { RatingSheet } from './screens/RatingSheet.tsx';
import { DeliveryCodePrompt } from './screens/DeliveryCodePrompt.tsx';
import { SearchScreen } from './screens/SearchScreen.tsx';
import { SignInSheet } from './screens/SignInSheet.tsx';
import { SupportScreen } from './screens/SupportScreen.tsx';
import { addressBookStore, missingTo, tariffOf } from './state/address-book.ts';
import { cartStore, itemsCount, itemsTotal } from './state/cart.ts';
import { setCartFlightTarget } from './state/cart-flight.ts';
import { addressGateStore, ageGateStore, dismissAgeGate } from './state/gates.ts';
import { partnerPromoStore } from './state/partner-promo.ts';
import { LAUNCH_KEYS, runLaunch } from './state/launch.ts';
import { pushStore } from './state/push.ts';
import {
  closeSheet,
  openCart,
  openOrder,
  selectTab,
  showsProductScreen,
  useRouter,
  visibleOrderId,
  type Route,
  type Tab,
} from './state/router.ts';
import { searchQueryStore } from './state/search.ts';
import { restoreSession, useSession } from './state/session.ts';
import { dismissCodePrompt, dismissNotice, dismissRating, useWatcher } from './state/watcher.ts';
import { dismissPaymentStatus, paymentStatusStore } from './state/payment.ts';
import { Icon, type IconName } from './ui/icons.tsx';
import { Button, cx, ProgressBar, Skeleton, Spinner, ToastLayer } from './ui/kit.tsx';
import { noDockEntries, ScreenPlaceContext } from './ui/Screen.tsx';

/**
 * Корень приложения: пока сессия неизвестна — скелет главной, дальше витрина. Витрина открыта
 * гостю; вход поднимается листом на оформлении или из профиля.
 */
export function App() {
  const session = useSession();
  if (session.status === 'unknown') return <Splash failed={session.restoreFailed} />;
  return <Shell />;
}

/**
 * Состояние запуска, пока проверяется токен: скелет главной, в который потом встаёт
 * содержимое. Если сервер не ответил, повтор здесь же: живой токен не должен вести к новому
 * входу по SMS.
 */
function Splash({ failed }: { failed: boolean }) {
  const strings = useStrings();
  return (
    <div className="app">
      <div className="screen no-dock">
        <div className="page stack gutter" style={{ gap: 20, paddingTop: 4 }}>
          <Skeleton width={220} height={46} radius={23} style={{ alignSelf: 'center' }} />
          <Skeleton height={170} />
          <div className="row" style={{ gap: 10 }}>
            <Skeleton height={210} style={{ flex: 1 }} />
            <Skeleton height={210} style={{ flex: 1 }} />
            <Skeleton height={210} style={{ flex: 1 }} />
          </div>
          {failed && (
            <div className="stack" style={{ gap: 12, alignItems: 'center', paddingTop: 20 }}>
              <p className="t-muted" style={{ textAlign: 'center' }}>
                {strings.errors.offline ?? errorText(strings, null)}
              </p>
              <Button kind="secondary" className="splash-retry" onClick={() => void restoreSession()}>
                {strings.retry}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const TABS: Tab[] = ['home', 'catalog', 'profile', 'search'];

function rootScreen(tab: Tab): ReactNode {
  switch (tab) {
    case 'home':
      return <HomeScreen />;
    case 'catalog':
      return <CatalogScreen />;
    case 'profile':
      return <ProfileScreen />;
    case 'search':
      return <SearchScreen />;
  }
}

function routeScreen(route: Route): ReactNode {
  switch (route.name) {
    case 'category':
      return <CategoryScreen category={route.category} />;
    case 'list':
      return <ProductListScreen source={route.source} title={route.title} />;
    case 'product':
      return <ProductScreen item={route.item} />;
    case 'notifications':
      return <NotificationsScreen />;
    case 'orders':
      return <OrdersScreen />;
    case 'order':
      return <OrderScreen orderId={route.orderId} />;
    case 'addresses':
      return <AddressesScreen />;
    case 'cards':
      return <CardsScreen />;
    case 'newAddress':
      return <NewAddressScreen />;
    case 'support':
      return <SupportScreen orderId={route.orderId} />;
  }
}

/**
 * Вкладки приложения. Полоса корзины живёт над панелью вкладок; здесь же баннер о смене
 * статуса заказа и листы, которые видны над любой вкладкой.
 */
function Shell() {
  const strings = useStrings();
  const router = useRouter();
  const session = useSession();
  const watcher = useWatcher();
  const cart = useStore(cartStore);
  const ageGate = useStore(ageGateStore);
  const addressGate = useStore(addressGateStore);
  const partnerPromo = useStore(partnerPromoStore);
  const pushPrimer = useStore(pushStore).primerPending;

  const openOrderId = visibleOrderId(router);
  const accessoryOrders = watcher.active.filter((order) => order.id !== openOrderId);
  const onProduct = showsProductScreen(router);
  const noDock = useStore(noDockEntries);
  const topEntry = router.stacks[router.tab].at(-1);
  const hidesDock = topEntry !== undefined && noDock.has(topEntry.key);
  // С открытым поиском полосе нет места: она встала бы между полем и клавиатурой.
  const showsAccessory = !hidesDock && router.tab !== 'search' && (cart.lines.length > 0 || accessoryOrders.length > 0);
  const cartOpen = router.sheets.includes('cart');

  useLaunchLink();

  const notice = watcher.notice && watcher.notice.orderId !== openOrderId ? watcher.notice : null;
  const paymentStatus = useStore(paymentStatusStore);

  return (
    <div
      className={cx('app', onProduct && 'is-product')}
      style={{ '--dock-h': `calc(var(--tabbar-h) + 12px + ${showsAccessory ? 60 : 0}px + var(--safe-bottom))` } as CSSProperties}
    >
      {TABS.map((tab) => {
        const stack = router.stacks[tab];
        return (
          <div key={tab} className="tab-pane" hidden={router.tab !== tab} inert={router.tab !== tab}>
            <ScreenPlaceContext value={{ root: true, covered: stack.length > 0 }}>{rootScreen(tab)}</ScreenPlaceContext>
            {stack.map((entry, index) => (
              <ScreenPlaceContext key={entry.key} value={{ root: false, covered: index < stack.length - 1, entryKey: entry.key }}>
                {routeScreen(entry.route)}
              </ScreenPlaceContext>
            ))}
          </div>
        );
      })}

      <div className={cx('dock', hidesDock && 'is-hidden')}>
        {showsAccessory &&
          (cart.lines.length > 0 ? <CartAccessory strings={strings} /> : accessoryOrders[0] && <ActiveOrderAccessory order={accessoryOrders[0]} strings={strings} />)}
        {router.tab === 'search' ? <SearchDock strings={strings} back={router.previousTab} /> : <TabBar strings={strings} tab={router.tab} />}
      </div>

      {paymentStatus && (
        <button
          key={`pay-${paymentStatus.id}`}
          type="button"
          className={cx('status-notice payment-notice', `is-${paymentStatus.phase}`)}
          onClick={() => {
            dismissPaymentStatus();
            openOrder(paymentStatus.orderId);
          }}
        >
          {paymentStatus.phase === 'paying' ? <Spinner size={18} /> : <Icon name={paymentStatus.phase === 'paid' ? 'check' : 'info'} size={20} className="status-notice-icon" />}
          <span className="grow">{paymentStatus.phase === 'paying' ? strings.paymentInProgress : paymentStatus.phase === 'paid' ? strings.paymentDone : strings.paymentProblem}</span>
          {paymentStatus.phase === 'problem' && <Icon name="chevronRight" size={14} weight={2.8} className="status-notice-chevron" />}
        </button>
      )}
      {notice && !paymentStatus && (
        <button
          key={notice.id}
          type="button"
          className="status-notice"
          onClick={() => {
            dismissNotice();
            openOrder(notice.orderId);
          }}
        >
          <Icon name="box" size={20} className="status-notice-icon" />
          <span className="grow">{statusText(strings, notice.status)}</span>
          <Icon name="chevronRight" size={14} weight={2.8} className="status-notice-chevron" />
        </button>
      )}

      {cartOpen && <CartSheet onClose={() => closeSheet('cart')} />}
      {router.sheets.includes('signIn') && <SignInSheet onClose={() => closeSheet('signIn')} />}
      {/* Подтверждение возраста над витриной; из открытой корзины его показывает сама корзина. */}
      {ageGate.pending && !cartOpen && <AgeGateSheet key={ageGate.pending.id} request={ageGate.pending} onClose={dismissAgeGate} />}
      {/* Первый «в корзину» спрашивает адрес: лист с картой над витриной. */}
      {addressGate && <AddressGateSheet key={addressGate.id} request={addressGate} onClose={() => addressGateStore.set(null)} />}
      {watcher.ratingPrompt && session.profile && (
        <RatingSheet key={watcher.ratingPrompt.id} order={watcher.ratingPrompt} onClose={(stars) => dismissRating(stars !== null)} />
      )}
      {/* Курьер у двери ждёт код: окно с кодом поверх всего. */}
      {watcher.codePrompt && <DeliveryCodePrompt key={watcher.codePrompt.orderId + watcher.codePrompt.code} prompt={watcher.codePrompt} onClose={dismissCodePrompt} />}
      {partnerPromo.showsInterstitial && <PartnerInterstitial />}
      {/* После первого оплаченного заказа: зачем уведомления, затем системный вопрос браузера. */}
      {pushPrimer && !partnerPromo.showsInterstitial && <PushPrimerSheet />}
      <ToastLayer />
    </div>
  );
}

function TabBar({ strings, tab }: { strings: Strings; tab: Tab }) {
  const item = (target: Tab, icon: IconName, title: string) => (
    <button type="button" role="tab" aria-selected={tab === target} className={cx('tabbar-item', tab === target && 'is-selected')} onClick={() => selectTab(target)}>
      <Icon name={icon} size={24} />
      <span>{title}</span>
    </button>
  );
  return (
    <nav className="tabbar-row">
      <div className="tabbar" role="tablist">
        {item('home', 'home', strings.homeTab)}
        {item('catalog', 'grid', strings.catalogTab)}
        {item('profile', 'person', strings.profileTitle)}
      </div>
      <button
        type="button"
        className={cx('tabbar-search', tab === 'search' && 'is-selected')}
        aria-label={strings.searchTitle}
        aria-pressed={tab === 'search'}
        onClick={() => selectTab('search')}
      >
        <Icon name="search" size={24} weight={2.4} />
      </button>
    </nav>
  );
}

const TAB_ICONS: Record<Tab, IconName> = { home: 'home', catalog: 'grid', profile: 'person', search: 'search' };

/**
 * Вкладка поиска: панель вкладок сворачивается в одну кнопку возврата, а её место занимает
 * поле ввода. Поле берёт фокус, пока на вкладке нет открытых экранов.
 */
function SearchDock({ strings, back }: { strings: Strings; back: Tab }) {
  const query = useStore(searchQueryStore);
  const input = useRef<HTMLInputElement>(null);
  const target = back === 'search' ? 'home' : back;

  useEffect(() => {
    input.current?.focus({ preventScroll: true });
  }, []);

  return (
    <div className="search-dock">
      <button
        type="button"
        className="tabbar-search"
        aria-label={strings.close}
        onClick={() => {
          // Уход из поиска завершает его: следующий поиск начинается с чистого поля.
          searchQueryStore.set('');
          selectTab(target);
        }}
      >
        <Icon name={TAB_ICONS[target]} size={24} />
      </button>
      <label className="search-field">
        <Icon name="search" size={19} weight={2.2} />
        <input
          ref={input}
          type="search"
          enterKeyHint="search"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          placeholder={strings.searchProducts}
          value={query}
          onChange={(event) => searchQueryStore.set(event.target.value)}
        />
        {query !== '' && (
          <button
            type="button"
            className="search-clear"
            aria-label={strings.a11yClearText}
            onClick={() => {
              searchQueryStore.set('');
              input.current?.focus();
            }}
          >
            <Icon name="close" size={12} weight={3.4} />
          </button>
        )}
      </label>
    </div>
  );
}

/** Полоса корзины над вкладками: число позиций, подсказка до бесплатной доставки и сумма. */
function CartAccessory({ strings }: { strings: Strings }) {
  const cart = useStore(cartStore);
  const tariff = tariffOf(useStore(addressBookStore));
  const total = itemsTotal(cart);
  const element = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    setCartFlightTarget(element.current);
    return () => setCartFlightTarget(null);
  }, []);

  // Подсказка меняется по мере наполнения: минимум заказа важнее порога бесплатной доставки.
  // Рядом с суммой фраза целиком помещается не всегда: на узком экране остаётся одна сумма,
  // а смысл досказывает полоса прогресса.
  const toMinimum = missingTo(tariff?.minOrderRsd, total);
  const toFree = missingTo(tariff?.freeFromRsd, total);
  const missing = toMinimum ?? toFree;
  const settled = tariff && para(tariff.freeFromRsd) > 0n ? strings.deliveryFree : strings.cartTitle;
  const hint = missing === null ? settled : toMinimum !== null ? strings.toMinimumShort(formatPara(missing)) : strings.toFreeDeliveryShort(formatPara(missing));
  const shortHint = missing === null ? settled : strings.amountMore(formatPara(missing));
  const freeFrom = tariff ? para(tariff.freeFromRsd) : 0n;

  return (
    <button type="button" ref={element} className="accessory" onClick={openCart}>
      <span className="accessory-count t-numeric">{itemsCount(cart)}</span>
      <span className="grow stack" style={{ gap: 4 }}>
        <span className="accessory-title">
          <span className="accessory-status-full">{hint}</span>
          <span className="accessory-status-short">{shortHint}</span>
        </span>
        {freeFrom > 0n && <ProgressBar progress={ratio(total, freeFrom)} height={3} />}
      </span>
      <span className="accessory-total t-numeric">{formatPara(total)}</span>
    </button>
  );
}

/** Полоса заказа в работе: статус и время, тап открывает заказ. */
function ActiveOrderAccessory({ order, strings }: { order: OrderListItem; strings: Strings }) {
  const lang = useLang();
  const expected = order.expectedAt && !OrderStatusGroup.awaitingPayment.has(order.status) ? order.expectedAt : null;
  return (
    <button type="button" className="accessory" onClick={() => openOrder(order.id)}>
      <span className="accessory-count is-soft">
        <Icon name={OrderStatusGroup.onTheWay.has(order.status) ? 'bike' : 'box'} size={16} />
      </span>
      <span className="grow accessory-title">
        <span className="accessory-status-full">{statusText(strings, order.status)}</span>
        <span className="accessory-status-short">{shortStatusText(strings, order.status)}</span>
      </span>
      {expected && (
        <span className="accessory-eta t-numeric">
          {new Date(expected).getTime() < Date.now() ? strings.arrivesSoon : `~${clockTime(lang, expected)}`}
        </span>
      )}
      <Icon name="chevronRight" size={14} weight={2.8} className="t-muted" />
    </button>
  );
}

/** Ссылка запуска выполняется один раз и убирается из адреса. */
function useLaunchLink(): void {
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const params = new URLSearchParams(location.search);
    if (!runLaunch(params)) return;
    for (const key of LAUNCH_KEYS) params.delete(key);
    const rest = params.toString();
    history.replaceState(history.state, '', `${location.pathname}${rest ? `?${rest}` : ''}`);
  }, []);
}
